<template>
  <smart-dialog :title="dialogTitle" :value="value" :width="632" @input="onDialogInput">
    <v-container v-if="context !== 'DELETE'">
      <v-form ref="workshopform" v-model="valid" @submit.prevent="onSubmit">
        <p class="font-weight-medium mt-1 mb-2">Informations principales</p>

        <v-row dense class="px-4 pt-4">
          <v-col cols="12">
            <v-text-field
                v-model.trim="localWorkshop.place"
                dense
                label="Nom ou lieu"
                :rules="rules.nonEmptyTrim('Lieu requis')"
            />
          </v-col>

          <v-col cols="8">
            <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290"
                min-width="290"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                    v-model="startDateInput"
                    label="Date"
                    prepend-icon="mdi-calendar"
                    dense
                    :disabled="localWorkshop.status === 'closed'"
                    v-bind="attrs"
                    v-on="on"
                    :rules="[validateFrenchDate]"
                    @blur="syncTypedStartDate"
                    @keydown.enter.prevent="syncTypedStartDate"
                />
              </template>

              <v-date-picker
                  v-model="localWorkshop.startDate"
                  locale="fr-fr"
                  no-title
                  :disabled="localWorkshop.status === 'closed'"
                  @input="onPickStartDate"
              />
            </v-menu>
          </v-col>

          <v-col cols="4">
            <v-text-field
                v-model="startTimeInput"
                label="Heure"
                dense
                prepend-icon="mdi-clock"
                placeholder="14h00"
                :disabled="localWorkshop.status === 'closed'"
                :rules="[v => !v || !!_parseHourInput(v) || 'Heure invalide']"
            />
          </v-col>
        </v-row>

        <v-divider class="my-4" />

        <p class="font-weight-medium mt-1 mb-2">Informations complémentaires</p>

        <v-row dense class="px-4 pt-4">
          <v-col cols="2">
            <v-text-field
                v-model="costInput"
                label="Coût"
                placeholder="120,50 €"
                :rules="[validateCost]"
                @blur="syncCostInput"
            />
          </v-col>

          <v-col cols="12">
            <v-textarea
                v-model="localWorkshop.comment"
                label="Commentaires"
                auto-grow
            />
          </v-col>
        </v-row>
      </v-form>
    </v-container>

    <v-container v-else>
      <v-row class="justify-center">
        <v-col cols="2" class="d-flex align-center justify-center">
          <v-icon color="blue darken-2" size="80">mdi-information-slab-box</v-icon>
        </v-col>

        <v-col cols="10">
          <p class="font-weight-medium mb-1 grey--text">Confirmer la suppression</p>
          <p>Supprimer un workshop aura pour conséquence de supprimer également chacun des éléments qui lui sont associés.</p>
          <p>Ainsi les sessions et les contacts créés au cours de l'évènement seront aussi supprimés.</p>
          <p class="mt-1 font-weight-medium">Le workshop ne pourra pas être restauré après suppression.</p>
        </v-col>
      </v-row>
    </v-container>

    <template #actions>
      <smart-btn secondary @click.native="closeDialog">
        Annuler
      </smart-btn>

      <smart-btn
          v-if="context === 'EDIT' || context === 'CREATE'"
          primary
          :disabled="!valid || loading"
          @click.native="onSubmit"
      >
        {{ context === 'EDIT' ? 'Enregistrer' : 'Créer le workshop' }}
      </smart-btn>

      <smart-btn
          v-else
          primary
          action="delete"
          :disabled="loading"
          @click.native="onDelete"
      >
        Supprimer
      </smart-btn>
    </template>
  </smart-dialog>
</template>

<script>
import { post } from "@/utils/api"
import FormMixin from "@/components/mixins/FormMixin"
import { EventBus } from "@/components/commons/event-bus"

export default {
  name: "WorkshopEditDialog",
  mixins: [FormMixin],
  components: {  },
  emits: ['input', 'saved', 'deleted', 'created'],
  props: {
    value: { type: Boolean, default: false },   // ouverture du dialog
    workshop: { type: Object, required: true },
    context: { type: String, required: true }   // CREATE | EDIT | DELETE
  },
  data() {
    return {
      localWorkshop: this.buildInitialWorkshop(),
      loading: false,
      valid: true,
      costInput: "",
      startDateInput: "",
      startTimeInput: "09h00",
      dateMenu: false
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(opened) {
        if (opened) this.resetFromProps()
      }
    },
    workshop: {
      deep: true,
      handler() {
        if (this.value) this.resetFromProps()
      }
    }
  },
  computed: {
    dialogTitle() {
      if (this.context === "CREATE") return "Nouveau workshop"
      if (this.context === "DELETE") return "Supprimer le workshop"
      return "Modifier le workshop"
    },
    fullStartDate() {
      return this._combineDateAndHour(this.localWorkshop.startDate, this.startTimeInput, true)
    }
  },
  methods: {
    buildInitialWorkshop() {
      // EDIT / DELETE
      if (this.workshop && this.workshop.id) {
        return { ...this.workshop }
      }

      // CREATE
      return {
        id: null,
        place: "",
        userId: -1,
        status: "not_started",
        comment: null,
        cost: null,
        startDate: this._formatDate(new Date(), "yyyy-MM-dd"),
        stopDate: null
      }

    },
    resetFromProps() {
      const base = this.buildInitialWorkshop()
      const isCreate = !base.id
      const start = base.startDate ? this._parseDateISO(base.startDate) : new Date()

      this.localWorkshop = { ...base, startDate: this._formatDate(start, "yyyy-MM-dd")}
      this.startDateInput = this._formatDate(start, this.datePatternConfig.fr.localDate)
      this.startTimeInput = isCreate ? "09h00" : this._formatDate(start, "HH'h'mm")
      this.costInput = this._formatCurrency(base.cost, "EUR")
      this.loading = false
      this.valid = true
      this.dateMenu = false
    },
    closeDialog() {
      this.$emit("input", false)
    },
    onDialogInput(value) {
      this.$emit("input", value)
    },
    onPickStartDate(value) {
      this.localWorkshop.startDate = value
      this.startDateInput = this._parseAndFormatDate(
          value,
          "yyyy-MM-dd",
          this.datePatternConfig.fr.localDate
      )
      this.dateMenu = false
    },
    syncTypedStartDate() {
      if (!this.startDateInput) {
        this.localWorkshop.startDate = null
        return
      }

      const parsed = this._parseDate(this.startDateInput, this.datePatternConfig.fr.localDate)
      if (!this._toDate(parsed) || isNaN(parsed.getTime())) return

      this.localWorkshop.startDate = this._formatDate(parsed, "yyyy-MM-dd")
      this.startDateInput = this._formatDate(parsed, this.datePatternConfig.fr.localDate)
    },
    validateFrenchDate(value) {
      if (!value) return true

      const parsed = this._parseDate(value, this.datePatternConfig.fr.localDate)
      return (parsed instanceof Date && !isNaN(parsed.getTime())) || "Date invalide"
    },
    parseFrenchCost(value) {
      if (value === null || value === undefined) return null

      const raw = String(value).trim()
          .replace(/\s/g, "")
          .replace(/€/g, "")
          .replace(",", ".")
      if (raw === "") return null

      const number = Number(raw)
      if (Number.isNaN(number)) return null

      return number
    },
    validateCost(value) {
      if (!value) return true

      const parsed = this.parseFrenchCost(value)
      if (parsed === null) return "Coût invalide"

      return true
    },
    syncCostInput() {
      const parsed = this.parseFrenchCost(this.costInput)

      if (parsed === null) {
        this.localWorkshop.cost = null
        this.costInput = ""
        return
      }

      this.localWorkshop.cost = parsed
      this.costInput = this._formatCurrency(parsed, "EUR")
    },
    onSubmit() {
      if (!this.$refs.workshopform.validate()) return
      this.loading = true

      const payload = {
        ...this.localWorkshop,
        startDate: this.fullStartDate,
        userId: this.context === "CREATE" ? this.currentUser.id : this.localWorkshop.userId
      }
      const url = this.context === "CREATE" ? "/api/workshop" : `/api/workshop/${this.localWorkshop.id}`

      post(url, payload)
          .badRequest(() => {
            EventBus.$emit("toaster-error", "Une erreure est survenue à la création du workshop")
          })
          .json()
          .then((workshop) => {
            this.context === "CREATE" ? this.$emit("created", workshop) : this.$emit("saved")
            this.closeDialog()
          })
          .finally(() => {
            this.loading = false
          })
    },
    onDelete() {
      if (!this.localWorkshop.id) return
      this.loading = true
      post(`/api/workshop/${this.localWorkshop.id}/delete`)
          .badRequest(err => {
            EventBus.$emit("toaster-error", err.message)
          })
          .res(() => {
            this.$emit("deleted")
            this.closeDialog()
          })
          .finally(() => {
            this.loading = false
          })
    }
  },
}
</script>


<style scoped>

</style>